// @ts-check
import { MOTION } from "./tuning.js";

// Page gutters are often wider than QP; this is how far below a one-way top QP may still land on it.
const ONE_WAY_STEP = 6;

/** @typedef {{ x: number, y: number, vx: number, vy: number, jumps: number, grounded: boolean, crouching: boolean, sliding: boolean, pound: "none" | "windup" | "fall", poundTime: number, impact: number, boost: number, power: import("./types.js").Power, size:number, invulnerable:number, recoil:number, overclock:number, shotCooldown:number, facing:number }} Position */
/** @typedef {import("./types.js").Solid} Solid */
/** @typedef {{ width: number, height: number, endless?: boolean, left?: number, solids?: Solid[], gaps?: number[][], body?: {width: number, height: number}, speed?: number, gravity?:number, grip?: number, querySolids?: (left:number,right:number)=>Solid[], getBody?:()=>{width:number,height:number}, canStand?:()=>boolean, canCrawl?:(direction:number)=>boolean, onLand?: (solid:Solid)=>void, onHeadHit?: (solid: Solid) => void }} Bounds */
/** @typedef {{ sprint?: boolean, down?: boolean }} Controls */

/** @param {number} [x] @returns {Position} */
export function createState(x = 0) {
  return { x, y: 0, vx: 0, vy: 0, jumps: 0, grounded: true, crouching: false, sliding: false, pound: "none", poundTime: 0, impact: 0, boost: 0, power: "base", size: 1, invulnerable: 0, recoil: 0, overclock: 0, shotCooldown: 0, facing: 1 };
}

/** @param {number} x @param {number} width @param {Bounds} bounds */
export function hasFloor(x, width, bounds) {
  return !bounds.gaps?.some(([start, end]) => x >= start && x + width <= end);
}

/** @param {Position} state @param {Bounds} bounds */
function supported(state, bounds) {
  if (state.vy > 0) return false;
  const width = bounds.getBody?.().width ?? bounds.body?.width ?? 1;
  if (Math.abs(state.y) < 0.001 && hasFloor(state.x, width, bounds)) return true;
  return nearby(bounds, state.x, state.x + width).some((solid) => solid.active && state.x + width > solid.x + 0.001 && state.x < solid.x + solid.width - 0.001 && Math.abs(state.y - solid.y - solid.height) < 0.001);
}

/** @param {Bounds} bounds @param {number} left @param {number} right */
function nearby(bounds, left, right) { return bounds.querySolids?.(left, right) ?? bounds.solids ?? []; }

/**
 * Advance local movement in seconds. y is height above the floor.
 * @param {Position} state
 * @param {number} direction
 * @param {number} elapsed
 * @param {Bounds} bounds
 * @param {Controls} [controls]
 */
export function advance(state, direction, elapsed, bounds, controls = {}) {
  // Substeps keep acceleration and jump arcs consistent across refresh rates.
  let remaining = Math.min(Math.max(elapsed, 0), 0.05);
  while (remaining > 1e-9) {
    const dt = Math.min(remaining, 1 / 240);
    state.boost = Math.max(0, state.boost - dt);
    state.impact = Math.max(0, state.impact - dt);
    state.grounded = supported(state, bounds);
    const grounded = state.grounded;
    const forcedDuck = bounds.canStand?.() === false;
    const ducking = (grounded && !!controls.down || forcedDuck) && !state.impact;
    state.sliding = ducking && (state.sliding || Math.abs(state.vx) >= MOTION.slideThreshold) && Math.abs(state.vx) > MOTION.slideStop;
    state.crouching = ducking && !state.sliding;
    // A crouch facing a low gap crawls into it, so walking up and holding Down works too.
    const crawl = forcedDuck || ducking && !state.sliding && !!direction && !!bounds.canCrawl?.(direction);
    const locked = ducking && !crawl || state.pound !== "none" || state.impact > 0;
    if (direction && !state.recoil) state.facing = Math.sign(direction);
    const target = locked ? 0 : crawl ? direction * 95 : direction * (controls.sprint ? MOTION.sprint : MOTION.walk) * (bounds.speed ?? 1);
    // `bounds.grip` scales starting and stopping together with `bounds.speed`, so a faster pace never feels heavier.
    const acceleration = state.sliding ? MOTION.slideFriction : (direction && !locked ? MOTION.acceleration : MOTION.braking) * (bounds.grip ?? 1);
    if (!state.recoil) state.vx += Math.sign(target - state.vx) * Math.min(Math.abs(target - state.vx), acceleration * dt);
    const oldX = state.x;
    state.x += state.vx * dt;
    const body = bounds.getBody?.() ?? bounds.body ?? { width: 1, height: 1 };
    // Once below a fracture's rim, its walls prevent moving through the solid
    // floor beside it. Rising above the rim restores ordinary lateral travel.
    if (state.y < -0.001) for (const [start, end] of bounds.gaps ?? []) {
      if (oldX < start || oldX + body.width > end) continue;
      const inside = Math.max(start, Math.min(end - body.width, state.x));
      if (inside !== state.x) { state.x = inside; state.vx = 0; }
      break;
    }
    for (const solid of nearby(bounds, Math.min(oldX, state.x), Math.max(oldX, state.x) + body.width)) {
      // One-way solids (measured page elements) are ground only: their sides never block.
      if (!solid.active || solid.oneWay || state.y >= solid.y + solid.height - 0.001 || state.y + body.height <= solid.y + 0.001) continue;
      if (state.vx > 0 && oldX + body.width <= solid.x + 0.001 && state.x + body.width > solid.x) {
        state.x = solid.x - body.width; state.vx = 0;
      } else if (state.vx < 0 && oldX >= solid.x + solid.width - 0.001 && state.x < solid.x + solid.width) {
        state.x = solid.x + solid.width; state.vx = 0;
      }
    }
    const left = bounds.left ?? 0;
    if (!bounds.endless && (state.x <= left || state.x >= bounds.width)) {
      state.x = Math.max(left, Math.min(bounds.width, state.x));
      state.vx = 0;
    }
    state.grounded = supported(state, bounds);
    if (state.pound === "windup") {
      state.poundTime = Math.max(0, state.poundTime - dt);
      if (!state.poundTime) { state.pound = "fall"; state.vy = -MOTION.poundSpeed; }
    } else if (!state.grounded || state.vy !== 0 || state.pound === "fall") {
      const oldY = state.y;
      const gravity = state.pound === "fall" ? MOTION.poundGravity : MOTION.gravity * (bounds.gravity ?? 1);
      state.y += state.vy * dt - gravity / 2 * dt * dt;
      state.vy -= gravity * dt;
      let landing = state.y <= 0 && oldY >= -0.001 && hasFloor(state.x, body.width, bounds) ? 0 : null;
      /** @type {Solid|null} */ let head = null;
      /** @type {Solid|null} */ let landedOn = null;
      for (const solid of nearby(bounds, state.x, state.x + body.width)) {
        if (!solid.active || state.x + body.width <= solid.x + 0.001 || state.x >= solid.x + solid.width - 0.001) continue;
        const top = solid.y + solid.height;
        // A one-way top also catches feet that sank a few pixels while crossing a narrow gap beside it.
        if (state.vy <= 0 && oldY >= top - (solid.oneWay ? ONE_WAY_STEP : 0.001) && state.y <= top && top > (landing ?? -Infinity)) { landing = top; landedOn = solid; }
        if (state.vy > 0 && !solid.oneWay && oldY + body.height <= solid.y + 0.001 && state.y + body.height >= solid.y) {
          const distance = Math.abs(solid.x + solid.width / 2 - state.x - body.width / 2);
          if (!head || solid.y < head.y || solid.y === head.y && distance < Math.abs(head.x + head.width / 2 - state.x - body.width / 2)) head = solid;
        }
      }
      if (head) { state.y = head.y - body.height; state.vy = 0; bounds.onHeadHit?.(head); }
      if (landedOn && state.pound === 'fall') {
        bounds.onLand?.(landedOn);
        if (!landedOn.active) landing = null;
      }
      if (landing !== null) {
        state.y = landing;
        state.vy = 0;
        state.grounded = true;
        state.jumps = 0;
        state.boost = 0;
        if (state.pound === "fall") state.impact = MOTION.impactTime;
        state.pound = "none";
      }
    }
    remaining -= dt;
  }
}

/** @param {Position} state */
export function jump(state) {
  if (state.pound !== "none" || state.impact > 0 || state.recoil > 0) return false;
  const airborne = !state.grounded || state.vy > 0;
  if (airborne && state.jumps >= 2) return false;
  state.jumps = airborne ? 2 : 1;
  state.vy = airborne ? MOTION.doubleJump : MOTION.jump;
  state.boost = airborne ? MOTION.boostTime : 0;
  state.crouching = state.sliding = false;
  state.grounded = false;
  return true;
}

/** Begin one deliberate downward move; holding Down never repeats it.
 * @param {Position} state
 */
export function groundPound(state) {
  if (state.grounded || state.pound !== "none") return false;
  state.pound = "windup";
  state.poundTime = MOTION.poundWindup;
  state.vx = state.vy = state.boost = 0;
  return true;
}
